import { Cpu, Star, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { NumberTicker } from '@/components/effects';
import { aggregateGpuNodes, countGpuNodes } from '@/lib/collections/gpuNodes';
import { reputationPercent } from '@/utils/gpuNetwork';

interface NetworkStatsStripProps {
  className?: string;
}

/**
 * Lightweight, read-only network-health snapshot for the auto-matching
 * engine: total registered nodes, how many are currently active/matchable,
 * and the network's average reputation. Reads straight off the public
 * gpuNodes aggregate helpers — no extra backend route needed.
 */
export const NetworkStatsStrip: React.FC<NetworkStatsStripProps> = ({ className }) => {
  const [totalNodes, setTotalNodes] = useState(0);
  const [activeNodes, setActiveNodes] = useState(0);
  const [avgReputationBps, setAvgReputationBps] = useState(0);

  useEffect(() => {
    countGpuNodes().then((res) => setTotalNodes(res?.value ?? 0));
    countGpuNodes("status='active'").then((res) => setActiveNodes(res?.value ?? 0));
    aggregateGpuNodes('avg', { field: 'reputationScore', filter: "status='active'" }).then((res) =>
      setAvgReputationBps(res?.value ?? 0)
    );
  }, []);

  return (
    <div className={`grid grid-cols-3 gap-3 ${className ?? ''}`}>
      <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Cpu className="w-3 h-3" /> Total nodes
        </p>
        <p className="text-xl font-extrabold mt-1.5 tabular-nums">
          <NumberTicker value={totalNodes} />
        </p>
      </div>
      <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Zap className="w-3 h-3" /> Active nodes
        </p>
        <p className="text-xl font-extrabold mt-1.5 tabular-nums text-[#00C805]">
          <NumberTicker value={activeNodes} />
        </p>
      </div>
      <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Star className="w-3 h-3" /> Avg reputation
        </p>
        <p className="text-xl font-extrabold mt-1.5 tabular-nums">
          <NumberTicker value={reputationPercent(avgReputationBps)} />
          <span className="text-xs font-bold text-primary">%</span>
        </p>
      </div>
    </div>
  );
};

export default NetworkStatsStrip;
